import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { LocalStorageService } from '@core/services/local-storage/local-storage.service';
import { LookupCriteria } from '@core/services/lookup/lookup.model';
import { LookupService } from '@core/services/lookup/lookup.service';
import { UserPreferences } from '@core/services/user/user.model';
import { UserService } from '@core/services/user/user.service';
import { DataBindingDirective } from '@progress/kendo-angular-grid';
import { process } from '@progress/kendo-data-query';
import { ComponentGridSettings, FeatureComponentGridSettings, GridSettings } from '@shared/components/customized-grid/grid-settings/gird-settings.interface';
import { inputNoWhitespaceValidator } from '@shared/directives/input-no-whitespace.directive';
import { NgxSpinnerService } from 'ngx-spinner';
import { WorkQueueModel } from '../../core/services/work-queue/work-queue.model';

@Component({
  selector: 'Teranet-LookupPage',
  templateUrl: './lookup-page.component.html'
})
export class LookupPageComponent implements OnInit {
  @ViewChild(DataBindingDirective) dataBinding: DataBindingDirective;
  clientInfoId: number;
  componentPreferences: ComponentGridSettings[] = [];
  constructorName: string;
  featureComponentGridSettings: FeatureComponentGridSettings = new FeatureComponentGridSettings();
  isExternalUser: boolean = true;
  isFromAccountView: boolean = false;
  isPageReset = false;
  lookupCriteria = new LookupCriteria();
  query: string;
  lookupForm: FormGroup;
  lookups: WorkQueueModel[] = [];
  routerName: string;
  productAccountNumberDisabled: boolean = false;
  policyNumberDisabled: boolean = false;
  postalCodeDisabled: boolean = false;

  public gridSettings: GridSettings;
  public state: any = {
    skip: 0,
    take: 8,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  constructor(
    private localStorageService: LocalStorageService,
    private lookupService: LookupService,
    private spinner: NgxSpinnerService,
    private userService: UserService,
    private route: ActivatedRoute
  ) {
    this.lookupForm = new FormGroup({
      PAN: new FormControl('', [inputNoWhitespaceValidator, Validators.minLength(4)]),
      policyNumber: new FormControl(''),
      postalCode: new FormControl('')
    });
  }

  ngOnInit() {
    this.routerName = this.route.routeConfig.component.name;
    this.constructorName = this.constructor.name;
    this.featureComponentGridSettings.routerName = this.routerName;
    this.featureComponentGridSettings.featureName = this.constructorName;
    this.lookups = [];

    this.clientInfoId = this.localStorageService.getItem('currentUser')
      ? this.localStorageService.getItem('currentUser').corporationid >= 0
        ? this.localStorageService.getItem('currentUser').corporationid
        : -1
      : -1;

    if (this.clientInfoId === 0) {
      this.isExternalUser = false;
    }

    this.route.queryParams.subscribe(params => {
      if (params['from']) {
        if (params['from'] === 'accountview' && params['PAN'] !== undefined && params['policyNumber'] !== undefined && params['postalCode'] !== undefined) {
          this.lookupForm.get('PAN').setValue(params['PAN']?.toUpperCase());
          this.lookupForm.get('policyNumber').setValue(params['policyNumber']);
          this.lookupForm.get('postalCode').setValue(params['postalCode']);
          this.isFromAccountView = true;
          this.onSubmit();
        }
        else {
          this.onClearCriteria();
        }
      }
    });

    this.lookupForm.get('policyNumber').valueChanges.subscribe(x => {
      this.isPageReset = true;
      this.isFromAccountView = false;
    });
    this.lookupForm.get('PAN').valueChanges.subscribe(x => {
      this.isPageReset = true;
      this.isFromAccountView = false;
    });

    this.gridSettings = this.gridDefaultSettings();

    this.refresh();
  }

  isEnablePolicyNum() {
    const panctrl = this.lookupForm.get('PAN');
    const policytrl = this.lookupForm.get('policyNumber');
    const postalCodetrl = this.lookupForm.get('postalCode');

    if (policytrl.value && policytrl.value.trim().length > 0) {
      panctrl.disable();
      postalCodetrl.disable();
      this.productAccountNumberDisabled= false;
      this.policyNumberDisabled = true;
      this.postalCodeDisabled = false;
    } else {
      panctrl.enable();
      postalCodetrl.enable();
      this.productAccountNumberDisabled = true;
      this.policyNumberDisabled = true;
      this.postalCodeDisabled = true;

    }
  }
  isEnablePANNum() {
    const panctrl = this.lookupForm.get('PAN');
    const policytrl = this.lookupForm.get('policyNumber');
    const postalCodetrl = this.lookupForm.get('postalCode');
    if (panctrl.value && panctrl.value.trim().length > 0) {
      policytrl.disable();
      postalCodetrl.disable();
      this.productAccountNumberDisabled = true;
      this.policyNumberDisabled = false;
      this.postalCodeDisabled = false;

    } else {
      policytrl.enable();
      postalCodetrl.enable();
      this.productAccountNumberDisabled = true;
      this.policyNumberDisabled = true;
      this.postalCodeDisabled = true;

    }
  }
  isEnablePostalCode() {
    const panctrl = this.lookupForm.get('PAN');
    const policytrl = this.lookupForm.get('policyNumber');
    const postalCodetrl = this.lookupForm.get('postalCode');

    if (postalCodetrl.value && postalCodetrl.value.trim().length > 0) {
      panctrl.disable();
      policytrl.disable();
      this.productAccountNumberDisabled = false;
      this.policyNumberDisabled = false;
      this.postalCodeDisabled = true;

    } else {
      panctrl.enable();
      policytrl.enable();
      this.productAccountNumberDisabled = true;
      this.policyNumberDisabled = true;
      this.postalCodeDisabled = true;


    }
  }

  isEnableLookupBtn(): Boolean {
    var vin = this.lookupForm.get('PAN').value ? this.lookupForm.get('PAN').value.trim().length : 0;
    var AccountNumber = this.lookupForm.get('policyNumber').value ? this.lookupForm.get('policyNumber').value.trim().length : 0;
    var postalCode = this.lookupForm.get('postalCode').value ? this.lookupForm.get('postalCode').value.trim().length : 0;

    if ((vin >= 4 || AccountNumber > 0 || postalCode > 0) && (!this.lookupForm.invalid))  // Vin# must be at least 4 letter or number and Account # must be digital
    {
      return false;
    }

    return true;
  }

  

  onClearCriteria() {
    this.lookupForm.reset();
    this.lookupForm.get('PAN').enable();
    this.lookupForm.get('policyNumber').enable();
    this.lookupForm.get('postalCode').enable();
  }

  onSubmit() {
    this.spinner.show();
    this.lookupCriteria.productAccountNumber = this.lookupForm.get('PAN').value;
    this.lookupCriteria.policyNumber = this.lookupForm.get('policyNumber').value;
    this.lookupCriteria.postalCode = this.lookupForm.get('postalCode').value;

    if (this.componentPreferences && !this.isFromAccountView) {
      this.componentPreferences.find(p => p.componentName === this.constructorName).customizedSettings = this.lookupCriteria;
      if (this.componentPreferences.find(p => p.componentName === this.constructorName).gridSettings.length > 0) {
        this.componentPreferences.find(p => p.componentName === this.constructorName).gridSettings[0].state.skip = 0;
        this.gridSettings.state = this.componentPreferences.find(p => p.componentName === this.constructorName).gridSettings[0].state;
      }

      this.saveUserPreferences();
    }
    else if (this.isFromAccountView) {
      this.isFromAccountView = false;
    }

    this.query = this.lookupForm.get('PAN').value + this.lookupForm.get('policyNumber').value;

    this.lookupService.lookupByPolicyAndPostalCode(this.lookupCriteria).subscribe(value => {
      this.lookups = value.workQueueModels;
      if (this.isPageReset) {
        this.gridSettings.state = this.state;
      }
      this.gridSettings.gridData = process(this.lookups, this.state)
      this.spinner.hide();
    });
  }

  refresh() {
    this.userService.getUserDashboardPreferences(this.routerName).subscribe(data => {
      let isDefaultGrid: boolean = false;
      let componentGridSettings: ComponentGridSettings;
      if (data && data.length > 0) {
        this.componentPreferences = (data as any) as ComponentGridSettings[];
        if (
          this.componentPreferences.find(r => r.componentName === this.constructor.name) !=
          undefined
        ) {
          componentGridSettings = this.componentPreferences.find(
            r => r.componentName === this.constructor.name
          );
        } else {
          isDefaultGrid = true;
        }
      } else {
        isDefaultGrid = true;
      }
      if (isDefaultGrid) {
        componentGridSettings = new ComponentGridSettings();
        componentGridSettings.componentName = this.constructorName;
        componentGridSettings.gridSettings = [];
        componentGridSettings.gridSettings.push(this.gridDefaultSettings());
        this.componentPreferences.push(componentGridSettings);
      }
      this.featureComponentGridSettings.componentGridSettings = this.componentPreferences;
      let hasChange = this.resetCustomizedGrid(isDefaultGrid);
      if (hasChange) {
        this.saveUserPreferences();
      }
    });
  }

  resetCustomizedGrid(isDefaultGrid: boolean): boolean {
    let hasChange: boolean = false;

    // For Default Grid Reset
    //this.gridDefaultSettings().columnsConfig.find(
    //  x => x.field == 'corporationCode'
    //).includeInChooser = !this.isExternalUser;
    //this.gridDefaultSettings().columnsConfig.find(
    //  x => x.field == 'corporationCode'
    //).hidden = this.isExternalUser;

    // For Default Value Reset
    if (isDefaultGrid) {
      hasChange = true;

      //if (
        //this.gridSettings.columnsConfig.find(x => x.field == 'corporationCode').hidden !=
        //this.isExternalUser //||
        //this.gridSettings.columnsConfig.find(x => x.field == 'corporationCode').includeInChooser ==
        //this.isExternalUser
      //) {
        //this.gridSettings.columnsConfig.find(
        //  x => x.field == 'corporationCode'
        //).includeInChooser = !this.isExternalUser;
        //this.gridSettings.columnsConfig.find(
        //  x => x.field == 'corporationCode'
        //).hidden = this.isExternalUser;
      //}

      if (this.componentPreferences && this.componentPreferences.length > 0) {
        this.componentPreferences.find(
          p => p.componentName === this.constructor.name
        ).gridSettings[0] = this.gridSettings;
      }
    }
    else {
      // If new column is added
      this.gridSettings.columnsConfig.forEach(column => {
        if (
          this.componentPreferences
            .find(p => p.componentName === this.constructor.name)
            .gridSettings[0].columnsConfig.find(x => x.field == column.field) == null
        ) {
          hasChange = true;
          this.componentPreferences
            .find(p => p.componentName === this.constructor.name)
            .gridSettings[0].columnsConfig.push(column);
        }
      });
      // If existed column is removed from default grid SettingsService - TO DO => Clear the requirement, whether it requires to confirm with user
      // If default grid setting got changed, so User Preferenace need to update - TO DO

      // Read User Preferences & Reset the Grid
      if (this.componentPreferences && this.componentPreferences.length > 0) {
        this.gridSettings = this.componentPreferences.find(
          p => p.componentName === this.constructor.name
        ).gridSettings[0];
      }
    }
    this.gridSettings.state.skip = 0;

    // Remove Data from saving to minimize the DB storage
    if (hasChange && this.componentPreferences && this.componentPreferences.length > 0) {
      if (
        this.componentPreferences.find(p => p.componentName === this.constructor.name) &&
        this.componentPreferences.find(p => p.componentName === this.constructor.name)
          .gridSettings[0]
      ) {
        let gridData = { data: [], total: 0 };
        this.componentPreferences.find(
          p => p.componentName === this.constructor.name
        ).gridSettings[0].gridData = gridData;
      }
    }
    return hasChange;
  }

  saveUserPreferences() {
    const userPreferences: UserPreferences = {
      dashboardComponentName: this.routerName,
      dashboardPreferences: JSON.stringify(this.componentPreferences),
    };

    this.userService.createOrUpdateUserDashboardPreferences(userPreferences).subscribe();
  }

  gridDefaultSettings(): GridSettings {
    return {
      state: this.state,
      gridData: process(this.lookups,
        this.state
      ),
      columnsConfig: [
        {
          field: 'borrowersfirstname',
          filter: 'multicheck',
          filterable: true,
          hidden: this.isExternalUser,
          //includeInChooser: !this.isExternalUser,
          title: 'Borrowers First Name',
          width: 70,
        },
        {
          field: 'borrowerslastname',
          filterable: true,
          hidden: false,
          //includeInChooser: false,
          routerLink: '/accountview',
          title: 'Borrowers Last Name',
          width: 130,
        },
        {
          field: 'coborrower1firstname',
          filterable: true,
          hidden: false,
          //includeInChooser: false,
          routerLink: '/accountview',
          title: 'CoBorrower1 Firstname',
          width: 80,
        },
        {
          field: 'coborrower1lastname',
          filterable: true,
          hidden: false,
          title: 'CoBorrower1 Lastname',
          type: 'titlecase',
          width: 40,
        },
        {
          field: 'coborrower2firstname',
          filterable: true,
          hidden: false,
          //includeInChooser: false,
          routerLink: '/accountview',
          title: 'CoBorrower2 Firstname',
          width: 80,
        },
        {
          field: 'coborrower2lastname',
          filterable: true,
          hidden: false,
          title: 'CoBorrower2 Lastname',
          type: 'titlecase',
          width: 40,
        }, {
          field: 'coborrower3firstname',
          filterable: true,
          hidden: false,
          //includeInChooser: false,
          routerLink: '/accountview',
          title: 'CoBorrower3 Firstname',
          width: 80,
        },
        {
          field: 'coborrower3lastname',
          filterable: true,
          hidden: false,
          title: 'CoBorrower3 Lastname',
          type: 'titlecase',
          width: 40,
        },
        {
          field: 'lenderaddress',
          filterable: true,
          hidden: false,
          title: 'Property Address/Legal Address',
          type: 'titlecase',
          width: 40,
        },
        {
          field: 'lenderpostalcode',
          filterable: true,
          hidden: false,
          title: 'Property Postal Code',
          filter: 'multicheck',
          width: 40,
        },
        {
          field: 'appraisedvalue',
          filter: 'date',
          filterable: true,
          format: 'yyyy-MM-dd',
          hidden: false,
          title: 'Appraised Value/Principal Balance',
          width: 60,
          type: 'date',
        },
        {
          field: 'productaccountnumber',
          filterable: true,
          hidden: false,
          title: 'Product Account Number',
          width: 100,
        },
        {
          field: 'producttype',
          filterable: false,
          hidden: false,
          title: 'Product Type',
          width: 100,
        },
        {
          field: 'brand',
          filterable: false,
          hidden: false,
          title: 'Brand',
          width: 100,
        },
        {
          field: 'holcindicator',
          filterable: false,
          hidden: false,
          title: 'HPP/HOLC Indicator',
          width: 150,
        },
        {
          field: 'borrowerlanguagepreference',
          filterable: false,
          hidden: false,
          title: 'Borrower Language Preference',
          width: 150,
        },
        {
          field: 'rankofcharge',
          filterable: false,
          hidden: false,
          title: 'Rank of Charge',
          width: 150,
        },
        {
          field: 'status',
          filterable: false,
          hidden: false,
          title: 'Status(Active/Inactive)',
          width: 150,
        },
        {
          field: 'maturitydate',
          filterable: false,
          hidden: false,
          title: 'Maturity Date',
          width: 150,
        },
        {
          field: 'remainingamortization',
          filterable: false,
          hidden: false,
          title: 'Remaining Amortization',
          width: 150,
        },
        {
          field: 'insurecorporationcode',
          filterable: false,
          hidden: false,
          title: 'Insurance Company Name',
          width: 150,
        },
        {
          field: 'policynumber',
          filterable: false,
          hidden: false,
          title: 'Policy Number',
          width: 150,
        },
        {
          field: 'policyeffectivedate',
          filterable: false,
          hidden: false,
          title: 'Policy Effective Date',
          width: 150,
        },
        {
          field: 'noticetype',
          filterable: false,
          hidden: false,
          title: 'Notice Type (Policy)',
          width: 150,
        },
        {
          field: 'insuredfirstname',
          filterable: false,
          hidden: false,
          title: 'Insured First Name(s)',
          width: 150,
        },
        {
          field: 'insuredLastName',
          filterable: false,
          hidden: false,
          title: 'Insured Last Name(s)',
          width: 150,
        },
        {
          field: 'coinsured1firstname',
          filterable: false,
          hidden: false,
          title: 'CoInsured 1 First Name(s)',
          width: 150,
        },
        {
          field: 'coinsured1lastname',
          filterable: false,
          hidden: false,
          title: 'Co-Insured 1 Last Name(s)',
          width: 150,
        },
        {
          field: 'coinsured2firstname',
          filterable: false,
          hidden: false,
          title: 'CoInsured 2 First Name(s)',
          width: 150,
        },
        {
          field: 'coinsured2lastname',
          filterable: false,
          hidden: false,
          title: 'Co-Insured 2 Last Name(s)',
          width: 150,
        }, {
          field: 'coinsured3firstname',
          filterable: false,
          hidden: false,
          title: 'CoInsured 3 First Name(s)',
          width: 150,
        },
        {
          field: 'coinsured3lastname',
          filterable: false,
          hidden: false,
          title: 'Co-Insured 3 Last Name(s)',
          width: 150,
        },
        {
          field: 'dwellingcoverage',
          filterable: false,
          hidden: false,
          title: 'Dwelling Coverage',
          width: 150,
        }
      ]
    };
  }
}
